import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Megaphone } from "lucide-react";

const BLANK = { message: "", message_ar: "", link_url: "", link_label: "", link_label_ar: "", theme: "navy", active: true };
const THEMES = ["navy", "gold", "blue"];

export default function PromotionsAdmin() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["admin-banners"], queryFn: async () => (await api.get("/admin/banners")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => (editId ? api.put(`/admin/banners/${editId}`, form) : api.post("/admin/banners", form)),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-banners"] }); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const patchActive = useMutation({
    mutationFn: async ({ id, active }) => api.put(`/admin/banners/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-banners"] }),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/banners/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-banners"] }); },
  });

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...BLANK, ...b }); setEditId(b.id); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Promotions &amp; Banners</h1><p className="mt-1 text-sm text-subtle">Site-wide announcement bar shown above the header. Only one active banner displays.</p></div>
        <Button onClick={openNew} className="bg-finblue hover:bg-finblueDark" data-testid="banner-add"><Plus className="h-4 w-4" /> New Banner</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Message</TableHead><TableHead>Theme</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-subtle">No banners yet.</TableCell></TableRow>}
            {rows.map((b) => (
              <TableRow key={b.id} data-testid={`banner-row-${b.id}`}>
                <TableCell className="flex items-center gap-2 font-medium text-ink"><Megaphone className="h-4 w-4 text-finblue" />{b.message || "—"}</TableCell>
                <TableCell className="text-sm capitalize text-subtle">{b.theme}</TableCell>
                <TableCell><Switch checked={!!b.active} onCheckedChange={(v) => patchActive.mutate({ id: b.id, active: v })} data-testid={`banner-toggle-${b.id}`} /></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id}`}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del.mutate(b.id)} data-testid={`banner-delete-${b.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Banner" : "New Banner"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Message (English)" full><Input value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="banner-form-message" /></F>
            <F label="Message (Arabic)" full><Input dir="rtl" value={form.message_ar} onChange={(e) => set("message_ar", e.target.value)} /></F>
            <F label="Link URL"><Input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="/broker-partners" /></F>
            <F label="Link Label (EN)"><Input value={form.link_label} onChange={(e) => set("link_label", e.target.value)} placeholder="Learn more" /></F>
            <F label="Link Label (AR)"><Input dir="rtl" value={form.link_label_ar} onChange={(e) => set("link_label_ar", e.target.value)} /></F>
            <F label="Theme">
              <Select value={form.theme} onValueChange={(v) => set("theme", v)}>
                <SelectTrigger data-testid="banner-form-theme"><SelectValue /></SelectTrigger>
                <SelectContent>{THEMES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="flex items-center gap-3 sm:col-span-2"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="banner-form-active" /><Label>Active (show on site)</Label></div>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="banner-form-save">{save.isPending ? "Saving..." : "Save Banner"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
